/* eslint-disable @typescript-eslint/naming-convention */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import DropdownMenu from 'dropdown-menu'
import { getValue } from 'remote-config-value'
import StatusChip from '../../shared/StatusChip'
import { getFeatureEnabled } from 'feature-flag'
import CampaignsLandingPage from '../LandingPage'
import { makeStyles } from '@material-ui/core/styles'
import { Table } from 'foundations-library/components'
import ListContainer from '../../shared/ListContainer'
import { failedStatuses } from '../../../utils/statuses'
import DropdownButton from '../../shared/DropdownButton'
import BrandsContext from '../../../contexts/BrandsContext'
import ConfirmDeleteModal from '../../shared/ConfirmDeleteModal'
import { EnrollmentType } from '../../../interface/EnrollmentType'
import { CampaignContext } from '../../../contexts/CampaignContext'

const styles = makeStyles(() => {
    return {
        deleteMenuItem: {
            color: '#F5472C !important'
        }
    }
})

type MenuItem = {
    text: string
    onClick: () => void
    disabled?: boolean
    tooltipText?: string | null
    color?: string
    className?: string
    'data-testid': string
}

interface Props {
    onClickPreview(v): unknown
    onClickAddNew(): unknown
}
/**
 *
 */
const Enrollment = ({
    onStartDelete,
    onClickPreview,
    onClickAddNew
}: Props) => {
    const [deleteCampaignFeature, setDeleteCampaignFeature] = useState(false)
    const [editCampaignFeature, setEditCampaignFeature] = useState(false)

    const classes = styles()

    const {
        campaigns,
        fetchCampaigns,
        initialFetchParams,
        networkPending,
        deleteCampaign,
        setCampaignNetworkError,
        campaignNetworkError
    } = useContext(CampaignContext)

    const { enrollmentEnabled, brands } = useContext(BrandsContext)

    useEffect(() => {
        getFeatureEnabled('ten_dlc_delete_campaign_enabled').then(setDeleteCampaignFeature)
        getFeatureEnabled('ten_dlc_edit_campaign_enabled').then(setEditCampaignFeature)
    }, [])

    const getDeleteCampaignParams = (campaign) => ({
        csp_campaign_id: campaign.csp_campaign_id,
        mock: campaign.is_mock === 'Y',
        onSuccess: () => {
            fetchCampaigns(initialFetchParams)
        }
    })

    const includeEditInMenu = (row) => {
        return row.status !== '' && !['submitted', 'processing'].includes(row.status)
    }

    const [deleting, setDeleting] = useState(null)
    // const [submitPhase, setSubmitPhase] = useState<'idle' | 'waiting' | 'pending'>('idle')
    const [isDeleting, setIsDeleting] = useState(false)
    const deleteReachedNetworkPending = useRef(false)

    useEffect(() => {
        setCampaignNetworkError(null)
    }, [])

    const renderRegisteredOnColumn = (row) => {
        const createdDate = row.created_at
        const d = new Date(0)
        d.setUTCSeconds(createdDate)

        const year = d.toLocaleString('default', { year: 'numeric' })
        const month = d.toLocaleString('default', { month: '2-digit' })
        const day = d.toLocaleString('default', { day: '2-digit' })

        return `${year}-${month}-${day}`
    }

    const getTooltipText = (disableDelete, isNotRejected, hasDIDs, isDeleted) => {
        if (!disableDelete) return null
        if (isNotRejected) return getValue('sms_enrollment_campaign_delete_not_rejected_tooltip')
        if (hasDIDs) return getValue('sms_enrollment_campaign_delete_tooltip')
        if (isDeleted) return getValue('sms_enrollment_enrollment_status_deleted_tooltip')

        return null
    }

    const getDeleteCampaignOption = (row): MenuItem | null => {
        if (!deleteCampaignFeature) return null

        const hasDIDs = row.active_did_count > 0
        const isDeleted = row.deleted_at != null
        const isNotRejected = !row.status || !failedStatuses.includes(row.status)
        const disableDelete = hasDIDs || isDeleted || isNotRejected
        const tooltipText = getTooltipText(disableDelete, isNotRejected, hasDIDs, isDeleted)
        return {
            text: 'Delete',
            onClick: () => setDeleting(row),
            disabled: disableDelete,
            tooltipText: tooltipText,
            'data-testid': `item-delete-${row.csp_campaign_id}`
        }
    }

    const campaignEditDisabledHelper = (row) => {
        const associatedBrand = brands.find(x => row.csp_brand_id === x.csp_brand_id)
        const isDeleted = row.deleted_at != null
        const invalidBrandStatus = associatedBrand?.registration_status === 'pending-review'
        const disabled = isDeleted || invalidBrandStatus || !enrollmentEnabled

        return {
            editDisabled: disabled,
            editTooltip:
                disabled
                    ? (
                        !enrollmentEnabled
                            ? getValue('sms_enrollment_feature_disabled_tooltip')
                            : isDeleted
                                ? getValue('sms_enrollment_enrollment_status_deleted_tooltip')
                                : invalidBrandStatus
                                    ? getValue('sms_enrollment_enrollment_status_pending_tooltip')
                                    : null)
                    : null
        }
    }

    const renderOptionsColumn = (row) => {
        const menuItems: MenuItem[] = []

        const testUniqueIdentifier = row.csp_campaign_id || row.csp_brand_id || row.voip_did_id

        if (editCampaignFeature || includeEditInMenu(row)) {
            const { editDisabled, editTooltip } = campaignEditDisabledHelper(row)

            menuItems.push({
                text: 'Review/Edit',
                onClick: () => onClickPreview(row.csp_campaign_id),
                disabled: editDisabled,
                tooltipText: editDisabled ? editTooltip : null,
                'data-testid': `item-preview-${testUniqueIdentifier}`
            })
        }

        const deleteMenuItem = getDeleteCampaignOption(row)

        if (deleteMenuItem) {
            menuItems.push({
                ...deleteMenuItem,
                text: 'Delete',
                className: classes.deleteMenuItem,
                'data-testid': `item-delete-${testUniqueIdentifier}`
            })
        }

        return menuItems.length > 0
            ? (
                <DropdownMenu
                    button={DropdownButton}
                    menuItems={menuItems}
                    dropdownStyle='configure'
                    data-testid={`item-drop-down-${testUniqueIdentifier}`}
                />
            )
            : <></>
    }

    const rows = useMemo(
        () => campaigns?.map((row) => ([
            row.name,
            row.brand_name,
            renderRegisteredOnColumn(row),
            <StatusChip
                status={row.status}
                enrollmentType={EnrollmentType.Campaign}
                deleted={row.deleted_at != null}
                itemsAttached={row.active_did_count}
                key={row.csp_campaign_id}
                row-testid={`chip-${row.csp_campaign_id}`}
            />,
            renderOptionsColumn(row)
        ])) || [],
        [campaigns, deleteCampaignFeature, editCampaignFeature]
    )

    const onDeleteHandler = async () => {
        if (deleting == null || isDeleting) return
        setCampaignNetworkError(null)
        deleteReachedNetworkPending.current = false
        setIsDeleting(true)
        onStartDelete?.()
        try {
            const deleteParms = await getDeleteCampaignParams(deleting)
            deleteCampaign(deleteParms)
        } catch {
            setIsDeleting(false)
        }
    }

    useEffect(() => {
        if (isDeleting && networkPending) {
            deleteReachedNetworkPending.current = true
            return
        }

        if (isDeleting && !networkPending && (deleteReachedNetworkPending.current || campaignNetworkError)) {
            deleteReachedNetworkPending.current = false
            setDeleting(null)
            setIsDeleting(false)
        }
    }, [isDeleting, networkPending, campaignNetworkError])

    if (!campaigns.length) {
        return <CampaignsLandingPage onClickAddNew={onClickAddNew} />
    }

    return <ListContainer onClickAddNew={onClickAddNew} addButtonText={getValue('sms_enrollment_add_new_campaign')}>
        <Table
            columns={[
                getValue('sms_enrollment_campaign_id_column'),
                getValue('sms_enrollment_brand_column_name'),
                getValue('sms_enrollment_registered_column_name'),
                getValue('sms_enrollment_status_column_name'),
                ''
            ]}
            rows={rows}
            data-testid='sms-tabs-list-table'
        />
        <ConfirmDeleteModal
            isOpen={deleting != null}
            isLoading={isDeleting}
            type={'campaign'}
            selectedItem={deleting}
            onClose={() => {
                deleteReachedNetworkPending.current = false
                setDeleting(null)
                setIsDeleting(false)
            }}
            onDelete={onDeleteHandler}
        />
    </ListContainer>
}

export default Enrollment
